/**QueryFilter 组件内使用的三联查询条件**/
import React from "react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { Col, Form, Input, Row, Select } from "antd";
import QueryFilter from "@/components/Form/QueryFilter";
import { find, isArray, isNumber } from "lodash-es";
import storage from '@/utils/storage';

type QueryFilterConditionProps = {
  batchQueryMax?: number;
  searchConditions: {
    [key: string]: {
      label: any;
      value: any;
      condition?: string[];
      initialValue?: boolean
    }
  };
  conditionSort?: {
    storageKey?: string;
    lasterRecordsMax?: number;
  };
  batchSearch?: boolean;
  singleQueryMinLength?: number | { //单个查询时，限制查询key+条件，最少输入字符
    key: string,
    condition: string[],
    minLength: number
  }[]
}

const QueryFilterCondition = React.forwardRef(({
  searchConditions,
  batchQueryMax = 500,
  conditionSort,
  batchSearch = false,
  singleQueryMinLength
} :QueryFilterConditionProps,ref) => {
  const intl = useIntl();
  const searchForm = Form.useFormInstance();
  const [conditionKey, setConditionKey] = React.useState(0);
  const conditionQueryType = Form.useWatch('conditionQueryType', searchForm);
  const keyword = Form.useWatch('keyword', searchForm);
  const store = useLocalObservable(() => ({
    batchSearch: false,
    singleQueryPlaceholder:'',
    conditionOptions: [
      { value: 'equals', label: intl.formatMessage({ defaultMessage: '等于' }) },
      { value: 'begin', label: intl.formatMessage({ defaultMessage: '开头是' }) },
      { value: 'contain', label: intl.formatMessage({ defaultMessage: '包含' }) },
      { value: 'end', label: intl.formatMessage({ defaultMessage: '结尾是' }) },
    ] as any[],
    handleBatchSearch(val: boolean, force: boolean = false) {
      const keyvalue = searchForm.getFieldValue('keyvalue');
      if (!keyvalue || force) {
        this.batchSearch = val;
        if (val) {
          searchForm.setFieldValue('conditionQueryType', 'equals');
        }
      }
    },
    //单个查询
    setSingleQueryPlaceholder(conditionQueryType:string,keyword:string) {
      if (!this.batchSearch) {
        //单个查询默认限制字符
        this.singleQueryPlaceholder = intl.formatMessage({ defaultMessage: '至少输入{num}个字符，双击切换至批量查询' }, {
          num: ['end', 'contain'].includes(conditionQueryType) ? 2 : 1
        });
        //配置的单个查询限制字符
        if (singleQueryMinLength) {
          if (isNumber(singleQueryMinLength) && singleQueryMinLength > 0) {
            this.singleQueryPlaceholder = intl.formatMessage({ defaultMessage: '至少输入{num}个字符，双击切换至批量查询' }, {
              num: singleQueryMinLength
            });
            return;
          }
          if (isArray(singleQueryMinLength) && singleQueryMinLength?.length) {
            for (let i = 0; i < singleQueryMinLength?.length; i++){
              let min = singleQueryMinLength[i].minLength;
              let bolKey = singleQueryMinLength[i]?.key === keyword;
              let bolCondition = singleQueryMinLength[i]?.condition?.includes(conditionQueryType);
              if (bolKey && bolCondition) {
                this.singleQueryPlaceholder = intl.formatMessage({ defaultMessage: '至少输入{num}个字符，双击切换至批量查询' }, {
                  num: min
                });
                break;
              }
            }
          }
        }
      }
    },
    //conditionOptions 排序
    setSortConditionOptions() {
      let storageKey = conditionSort?.storageKey;
      if (storageKey) {
        let storageData = storage.get(storageKey) ?? {};
        this.conditionOptions.sort((a: any, b: any) =>
          (storageData?.[b.value]?.length || 0) - (storageData?.[a.value]?.length || 0));
        searchForm.setFieldValue('conditionQueryType',this.conditionOptions?.[0]?.value);
      }

    },
    //记录用户习惯：记录最新(conditionSort?.lasterRecordsMax || 19)次查询，缓存三联查询中的‘条件’使用次数，用于排序
    calcConditionQueryCount() {
    }
  }));
  React.useImperativeHandle(ref, () => {
    return {
      calcConditionQueryCount: store.calcConditionQueryCount,
    } as any;
  });

  React.useEffect(() => {
    if (conditionSort?.storageKey) {
      store.setSortConditionOptions();
    }
  }, []);

  React.useEffect(() => {
    store.handleBatchSearch(batchSearch, true);
  }, [batchSearch]);

  React.useEffect(() => {
    store.setSingleQueryPlaceholder(conditionQueryType,keyword);
  },[conditionQueryType,keyword]);

  return (
    <QueryFilter.Sort>
      <Form.Item noStyle>
        <Row>
          <Col span={12}>
            <Form.Item name="keyword" initialValue={
              find(searchConditions, { initialValue: true })?.value
              || Object.values(searchConditions)?.[0].value}>
              <Select options={Object.values(searchConditions)}
                onChange={() => {
                  setConditionKey(Math.random());
                  searchForm.setFieldValue('conditionQueryType','equals');
                }}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="conditionQueryType" initialValue='equals' key={conditionKey}>
              <Select options={store.conditionOptions.filter((item) => {
                let keyword: string = searchForm.getFieldValue('keyword');
                if (searchConditions?.[keyword]?.condition?.length) {
                  return searchConditions?.[keyword]?.condition?.includes(item?.value);
                }
                return true;
              })} disabled={store.batchSearch} />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item
        style={{marginLeft:'-16px'}}
        name="keyvalue"
        rules={[{
          async validator(rule, value) {
            const val = (value || '').split(/[\n,]+/).filter((item: string) => !!item.trim?.());
            if (store.batchSearch && val.length > batchQueryMax) {
              throw new Error(intl.formatMessage({ defaultMessage: '最多支持{num}个查询' }, { num: batchQueryMax }));
            }
            //单个查询
            if (!store.batchSearch) {
              let keyword: string = searchForm.getFieldValue('keyword');
              if (isNumber?.(singleQueryMinLength) && singleQueryMinLength > 0 && value && value.length < singleQueryMinLength) {
                throw new Error(intl.formatMessage({ defaultMessage: '至少输入{num}个字符' }, { num: singleQueryMinLength }));
              }
              if (isArray(singleQueryMinLength) && singleQueryMinLength?.length) {
                for (let i = 0; i < singleQueryMinLength?.length; i++){
                  let min = singleQueryMinLength[i].minLength;
                  let bolKey = singleQueryMinLength[i]?.key === keyword;
                  let bolCondition = singleQueryMinLength[i]?.condition?.includes(conditionQueryType);
                  if ( bolKey && bolCondition && value && value.length < min) {
                    throw new Error(intl.formatMessage({ defaultMessage: '至少输入{num}个字符' }, { num: min }));
                  }
                }
              }
              //单个查询默认限制字符
              if (['end', 'contain'].includes(conditionQueryType) && value && value.length < 2) {
                throw new Error(intl.formatMessage({ defaultMessage: '至少输入2个字符' }));
              }
            }
          },
        }]}
      >
        {
          store.batchSearch ? (
            <Input.TextArea
              style={{ paddingBottom:'12px'}}
              placeholder={intl.formatMessage({ defaultMessage: '最多查询{num}个，双击切换至单个查询' }, { num: batchQueryMax })}
              rows={1}
              onDoubleClick={() => store.handleBatchSearch(false)}
              showCount={{
                formatter: ({value}) => {
                  const val = (value || '').split(/[\n,]+/).filter((item: string) => !!item.trim?.());
                  return `${val.length}/${batchQueryMax}`;
                } }}
            />
          ) : (
            <Input
              placeholder={store.singleQueryPlaceholder}
              onDoubleClick={() => store.handleBatchSearch(true)}
            />
          )
        }
      </Form.Item>
    </QueryFilter.Sort>
  );
});

export default observer(QueryFilterCondition);
